/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    CompanyDetailsDto,
    CompanyStoresListDto,
    CompanyStoresListFilterParams,
} from '../api/companies/companiesClient.typings';
import {
    AiClassificationsHistoryDto,
    EntirePeriodTurnover,
    PageQueryParams,
    PeriodQueryParams,
    RegistryTurnoversByObjectsDto,
} from '../api/apiCommon.typings';
import { CompanyDetailsActions, CompanyDetailsActionType } from '../actions/companyDetailsActions';
import { storeStatus } from '../api/stores/storesClientTypings';
import { CashBoxesStats } from '../api/cashBoxes/cashBoxesClient.typings';
import {
    collapseEntirePeriodTurnovers,
    expandTurnoversPeriod,
    updateEntirePeriodTurnoverWithNewData,
} from '../../components/RegistryWidgets/Common/TurnoversUtils';
import { today } from './CashBoxRegistry/cashboxDetailsReducer';
import moment from 'moment';
import { TurnoverReportType } from '../../components/RegistryWidgets/Common/ListTypes';

export const rowsPerPage = [20, 30, 40];
export const defaultPaginationLimit = rowsPerPage[0];
export const defaultStoresStatusFilter: storeStatus[] = ['ACTIVE', 'SUSPENDED', 'CLOSED'];

export interface CompanyDetailsState {
    companyTaxNumber: string;

    isLoading: boolean;
    companyDetails: CompanyDetailsDto;

    isLoadingStoresList: boolean;
    companyStoresList: CompanyStoresListDto;
    storesPageParams: PageQueryParams;
    storesFilterParams: CompanyStoresListFilterParams;

    isLoadingCashBoxesStats: boolean;
    companyCashBoxesStats: CashBoxesStats;

    isLoadingAiClassificationsHistory: boolean;
    isDownloadingAiClassificationsHistory: boolean;
    aiClassificationsHistory: AiClassificationsHistoryDto;
    currentHistoryTab: number;

    companyTurnoversByPeriod: EntirePeriodTurnover;
    companyTurnoversByObjects: RegistryTurnoversByObjectsDto;
    turnoversByObjectsPageParams: PageQueryParams;
    turnoversByObjectsFilterParams: PeriodQueryParams;
    isLoadingTurnovers: boolean;
    isTurnoversTableHidden: boolean;
    turnoversReportType: TurnoverReportType;
}

export const initialState: CompanyDetailsState = {
    companyTaxNumber: null,
    companyDetails: null,
    companyCashBoxesStats: null,
    companyStoresList: null,
    storesPageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    storesFilterParams: {
        status: defaultStoresStatusFilter,
    },
    isLoading: false,
    isLoadingStoresList: false,
    isLoadingCashBoxesStats: false,

    isLoadingAiClassificationsHistory: false,
    isDownloadingAiClassificationsHistory: false,
    aiClassificationsHistory: null,

    currentHistoryTab: 0,

    companyTurnoversByPeriod: null,
    companyTurnoversByObjects: null,
    turnoversByObjectsPageParams: {
        limit: defaultPaginationLimit,
        offset: 0,
    },
    turnoversByObjectsFilterParams: {
        dateFrom: today.utc().clone().subtract(30, 'day').format(),
        dateTo: moment(new Date()).endOf('day').utc().format(),
    },
    isLoadingTurnovers: false,
    isTurnoversTableHidden: true,
    turnoversReportType: TurnoverReportType.ByPeriod,
};

function companyDetailsReducer(
    state: CompanyDetailsState = initialState,
    action: CompanyDetailsActions,
): CompanyDetailsState {
    switch (action.type) {
        case CompanyDetailsActionType.OPEN_REGISTRY_COMPANY_CARD_PAGE:
            const previousTaxNumber = state.companyTaxNumber;
            const currentTaxNumber = action.payload;

            let isNewCardOpen = true;
            if (previousTaxNumber && currentTaxNumber) isNewCardOpen = previousTaxNumber !== currentTaxNumber;

            return {
                ...state,
                isLoading: true,
                isLoadingStoresList: true,

                companyTaxNumber: action.payload,
                storesPageParams: isNewCardOpen ? initialState.storesPageParams : { ...state.storesPageParams },
                storesFilterParams: isNewCardOpen ? initialState.storesFilterParams : { ...state.storesFilterParams },

                companyTurnoversByPeriod:
                    isNewCardOpen || !state.companyTurnoversByPeriod ? null : { ...state.companyTurnoversByPeriod },
                companyTurnoversByObjects:
                    isNewCardOpen || !state.companyTurnoversByObjects ? null : { ...state.companyTurnoversByObjects },
                turnoversByObjectsPageParams: isNewCardOpen
                    ? initialState.turnoversByObjectsPageParams
                    : { ...state.turnoversByObjectsPageParams },
                turnoversByObjectsFilterParams: isNewCardOpen
                    ? initialState.turnoversByObjectsFilterParams
                    : { ...state.turnoversByObjectsFilterParams },
                isTurnoversTableHidden: isNewCardOpen
                    ? initialState.isTurnoversTableHidden
                    : state.isTurnoversTableHidden,
                turnoversReportType: isNewCardOpen ? initialState.turnoversReportType : state.turnoversReportType,
            };

        case CompanyDetailsActionType.APPLY_STORES_PAGING:
            return {
                ...state,
                isLoadingStoresList: true,
                storesPageParams: action.payload,
            };

        case CompanyDetailsActionType.APPLY_STORES_FILTER:
            return {
                ...state,
                isLoadingStoresList: true,
                storesFilterParams: action.payload,
                storesPageParams: { ...state.storesPageParams, offset: 0 },
            };

        case CompanyDetailsActionType.FETCH_COMPANY_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                companyDetails: action.payload,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_STORES_LIST_SUCCESS:
            return {
                ...state,
                isLoadingStoresList: false,
                companyStoresList: action.payload,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_CASHBOXES_STATS:
            return {
                ...state,
                isLoadingCashBoxesStats: true,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_CASHBOXES_STATS_SUCCESS:
            return {
                ...state,
                isLoadingCashBoxesStats: false,
                companyCashBoxesStats: action.payload,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_CASHBOXES_STATS_ERROR:
            return {
                ...state,
                isLoadingCashBoxesStats: false,
                companyCashBoxesStats: null,
            };

        case CompanyDetailsActionType.CHANGE_HISTORY_TAB:
            return {
                ...state,
                currentHistoryTab: action.payload,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_CARD_DATA_ERROR:
            return {
                ...state,
                isLoading: false,
                isLoadingStoresList: false,
                companyStoresList: {
                    count: 0,
                    list: [],
                },
            };

        case CompanyDetailsActionType.SET_TURNOVERS_HIDE_TOGGLE:
            return {
                ...state,
                isTurnoversTableHidden: action.payload,
            };

        case CompanyDetailsActionType.SET_TURNOVERS_REPORT_TYPE:
            return {
                ...state,
                turnoversReportType: action.payload,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_PERIOD:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_PERIOD_SUCCESS:
            return {
                ...state,
                isLoadingTurnovers: false,
                companyTurnoversByPeriod: updateEntirePeriodTurnoverWithNewData(
                    state.companyTurnoversByPeriod,
                    action.payload,
                ),
            };

        case CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_PERIOD_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
            };

        case CompanyDetailsActionType.COLLAPSE_TURNOVERS_BY_PERIOD:
            return {
                ...state,
                companyTurnoversByPeriod: collapseEntirePeriodTurnovers(state.companyTurnoversByPeriod),
            };

        case CompanyDetailsActionType.EXPAND_TURNOVERS_PERIOD:
            return {
                ...state,
                companyTurnoversByPeriod: expandTurnoversPeriod(
                    state.companyTurnoversByPeriod,
                    action.payload.period,
                    action.payload.isToHide,
                ),
            };

        case CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD_SUCCESS:
        case CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_OBJECTS:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_OBJECTS_SUCCESS:
            return {
                ...state,
                isLoadingTurnovers: false,
                companyTurnoversByObjects: action.payload,
            };

        case CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_OBJECTS_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
                companyTurnoversByObjects: {
                    count: 0,
                    turnovers: [],
                },
            };

        case CompanyDetailsActionType.APPLY_TURNOVERS_BY_OBJECTS_PAGING:
            return {
                ...state,
                isLoadingTurnovers: true,
                turnoversByObjectsPageParams: action.payload,
            };

        case CompanyDetailsActionType.APPLY_TURNOVERS_BY_OBJECTS_FILTER:
            return {
                ...state,
                isLoadingTurnovers: true,
                turnoversByObjectsFilterParams: action.payload,
                turnoversByObjectsPageParams: { ...state.turnoversByObjectsPageParams, offset: 0 },
            };

        case CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_OBJECTS:
            return {
                ...state,
                isLoadingTurnovers: true,
            };

        case CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_OBJECTS_SUCCESS:
        case CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_OBJECTS_ERROR:
            return {
                ...state,
                isLoadingTurnovers: false,
            };

        case CompanyDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_START:
            return {
                ...state,
                isLoadingAiClassificationsHistory: true,
            };

        case CompanyDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_SUCCESS:
            return {
                ...state,
                isLoadingAiClassificationsHistory: false,
                aiClassificationsHistory: action.payload,
            };

        case CompanyDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_ERROR:
            return {
                ...state,
                isLoadingAiClassificationsHistory: false,
                aiClassificationsHistory: {
                    count: 0,
                    items: [],
                },
            };

        case CompanyDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_START:
            return {
                ...state,
                isDownloadingAiClassificationsHistory: true,
            };

        case CompanyDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_SUCCESS:
        case CompanyDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_ERROR:
            return {
                ...state,
                isDownloadingAiClassificationsHistory: false,
            };

        case CompanyDetailsActionType.EXIT_REGISTRY_COMPANY_CARD_PAGE:
            return {
                ...state,
                companyDetails: null,
                companyStoresList: null,
                companyCashBoxesStats: null,
                aiClassificationsHistory: null,
                currentHistoryTab: 0,
            };

        default:
            return state;
    }
}

export default companyDetailsReducer;
